import api from './api';

export interface Permission {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions: Permission[];
  permission_ids?: number[];
  permission_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: number;
  user: number;
  user_name: string;
  user_email: string;
  role: number;
  role_name: string;
  company: number | null;
  company_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserPermission {
  id: number;
  user: number;
  user_name: string;
  user_email: string;
  permission: number;
  permission_name: string;
  permission_code: string;
  company: number | null;
  company_name: string | null;
  is_granted: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserWithPermissions {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  roles: Array<{
    id: number;
    role_id: number;
    role_name: string;
    company_id: number | null;
    company_name: string;
  }>;
  permissions: Array<{
    id: number;
    permission_id: number;
    permission_code: string;
    permission_name: string;
    company_id: number | null;
    company_name: string;
    is_granted: boolean;
  }>;
  all_permissions: string[];
}

export interface CurrentUserPermissions {
  success: boolean;
  user_id: number;
  is_superuser: boolean;
  current_company: {
    id: number;
    name: string;
  } | null;
  roles: Array<{
    id: number;
    name: string;
    company: string;
  }>;
  permissions: Permission[];
  permission_codes: string[];
}

// İcazələr
export const getPermissions = async (category?: string) => {
  const params = category ? { category } : {};
  const response = await api.get('/permissions/', { params });
  return response.data;
};

export const getPermissionCategories = async () => {
  const response = await api.get('/permissions/categories/');
  return response.data;
};

// Rollar
export const getRoles = async () => {
  const response = await api.get('/roles/');
  return response.data;
};

export const createRole = async (roleData: Partial<Role>) => {
  try {
    const response = await api.post('/roles/', roleData);
    return response.data;
  } catch (error) {
    console.error('Create role API error:', error);
    throw error;
  }
};

export const updateRole = async (id: number, roleData: Partial<Role>) => {
  const response = await api.put(`/roles/${id}/`, roleData);
  return response.data;
};

export const deleteRole = async (id: number) => {
  const response = await api.delete(`/roles/${id}/`);
  return response.data;
};

export const assignPermissionsToRole = async (roleId: number, permissionIds: number[]) => {
  const response = await api.post(`/roles/${roleId}/assign_permissions/`, {
    permission_ids: permissionIds
  });
  return response.data;
};

// İstifadəçi icazələri
export const getUserPermissions = async (userId?: number) => {
  const url = userId 
    ? `/manage-user-permissions/${userId}/`
    : '/manage-user-permissions/';
  const response = await api.get(url);
  return response.data;
};

export const assignRoleToUser = async (userId: number, roleId: number, companyId?: number) => {
  const response = await api.post('/manage-user-permissions/', {
    action: 'assign_role',
    user_id: userId,
    role_id: roleId,
    company_id: companyId
  });
  return response.data;
};

export const assignPermissionToUser = async (
  userId: number, 
  permissionId: number, 
  companyId?: number, 
  isGranted: boolean = true
) => {
  const response = await api.post('/manage-user-permissions/', {
    action: 'assign_permission',
    user_id: userId,
    permission_id: permissionId,
    company_id: companyId,
    is_granted: isGranted
  });
  return response.data;
};

export const removeRoleFromUser = async (userRoleId: number) => {
  const response = await api.post('/manage-user-permissions/', {
    action: 'remove_role',
    user_role_id: userRoleId
  });
  return response.data;
};

export const removePermissionFromUser = async (userPermissionId: number) => {
  const response = await api.post('/manage-user-permissions/', {
    action: 'remove_permission',
    user_permission_id: userPermissionId
  });
  return response.data;
};

// Hazırki istifadəçinin icazələri
export const getCurrentUserPermissions = async (): Promise<CurrentUserPermissions> => {
  try {
    const response = await api.get('/my-permissions/');
    return response.data;
  } catch (error) {
    console.error('API: getCurrentUserPermissions error:', error);
    throw error;
  }
};

// İcazə yoxlama
export const checkPermission = async (permissionCode: string) => {
  const response = await api.post('/check-permission/', {
    permission_code: permissionCode
  });
  return response.data;
};